import { formatNumberShorthand } from '../../../utils';

const DEFAULT_Y_AXIS_TICK_COUNT = 4;
const MIN_Y_AXIS_TICK_COUNT = 3;
const MAX_Y_AXIS_TICK_COUNT = 6;
const NICE_STEP_MULTIPLIERS = [1, 1.5, 2, 2.5, 3, 4, 5, 10];

export const formatChartNumber = (value: string | number, maximum_fraction_digits = 1): string => {
  const numeric_value = Number(value);

  if (!Number.isFinite(numeric_value)) {
    return 'N/A';
  }

  return numeric_value.toLocaleString(undefined, {
    maximumFractionDigits: maximum_fraction_digits,
  });
};

export const formatChartRateNumber = (
  value: string | number,
  rate_as?: 'second' | 'minute',
): string => {
  const numeric_value = Number(value);

  if (!Number.isFinite(numeric_value)) {
    return 'N/A';
  }

  return formatChartNumber(numeric_value, rate_as && Math.abs(numeric_value) > 10 ? 0 : 1);
};

export const formatChartAxisNumber = (value: string | number): string => {
  const numeric_value = Number(value);

  if (!Number.isFinite(numeric_value)) {
    return 'N/A';
  }

  const sign = numeric_value < 0 ? '-' : '';
  return `${sign}${formatNumberShorthand(Math.abs(numeric_value))}`;
};

const roundTickValue = (value: number, step: number): number => {
  const decimal_places = Math.max(0, Math.ceil(-Math.log10(step)) + 1);
  return Number(value.toFixed(Math.min(decimal_places, 12)));
};

const getNiceStepCandidates = (
  range: number,
  tick_count: number,
  allow_decimals: boolean,
): number[] => {
  const rough_step = range / Math.max(tick_count - 1, 1);
  const magnitude = 10 ** Math.floor(Math.log10(rough_step));
  const steps = new Set<number>();

  [magnitude / 10, magnitude, magnitude * 10].forEach((candidate_magnitude) => {
    NICE_STEP_MULTIPLIERS.forEach((multiplier) => {
      const step = multiplier * candidate_magnitude;
      steps.add(allow_decimals ? step : Math.max(1, Math.ceil(step)));
    });
  });

  return Array.from(steps).sort((a, b) => a - b);
};

export const getYAxisTicks = ({
  data_min = 0,
  data_max,
  allow_decimals,
  tick_count = DEFAULT_Y_AXIS_TICK_COUNT,
}: {
  data_min?: number;
  data_max: number;
  allow_decimals: boolean;
  tick_count?: number;
}): number[] => {
  const finite_data_min = Number.isFinite(data_min) ? data_min : 0;
  const finite_data_max = Number.isFinite(data_max) ? data_max : 0;
  const min = Math.min(0, finite_data_min);
  const max = Math.max(finite_data_max, 1);
  const range = max - min || 1;
  const candidates = getNiceStepCandidates(range, tick_count, allow_decimals)
    .map((step) => {
      const nice_min = min < 0 ? Math.floor(min / step) * step : 0;
      const nice_max = Math.ceil(max / step) * step;
      const ticks: number[] = [];

      for (let tick = nice_min; tick <= nice_max + step / 2; tick += step) {
        ticks.push(roundTickValue(tick, step));
      }

      return {
        step,
        ticks,
        score:
          (nice_max - max) / range +
          Math.abs(ticks.length - tick_count) * 0.05 +
          (ticks.length > tick_count ? 0.01 : 0),
      };
    })
    .filter(({ ticks }) => ticks.length >= MIN_Y_AXIS_TICK_COUNT)
    .filter(({ ticks }) => ticks.length <= MAX_Y_AXIS_TICK_COUNT)
    .sort((a, b) => a.score - b.score || a.step - b.step);

  if (candidates.length > 0) {
    return candidates[0].ticks;
  }

  const step = getNiceStepCandidates(range, tick_count, allow_decimals)[0] || 1;
  const nice_min = min < 0 ? Math.floor(min / step) * step : 0;
  const nice_max = Math.ceil(max / step) * step;
  const ticks: number[] = [];

  for (let tick = nice_min; tick <= nice_max + step / 2; tick += step) {
    ticks.push(roundTickValue(tick, step));
  }
  return ticks;
};
